// src/components/leads/LeadFiltersPanel.tsx
import React, { useState } from 'react';
import { LeadFilters } from '../../services/lead.service';
import { LeadStatus, LeadDetermination } from '../../types/lead.types';
import { SchoolSegment } from '../../types/project.types';
import { segmentDotClass } from '../../utils/segmentColors';
import { format } from 'date-fns';

export interface LeadFiltersPanelProps {
  filters: LeadFilters;
  onFiltersChange: (filters: LeadFilters) => void;
  segments?: SchoolSegment[];
}

const LeadFiltersPanel: React.FC<LeadFiltersPanelProps> = ({
  filters,
  onFiltersChange,
  segments
}) => {
  const [localFilters, setLocalFilters] = useState<LeadFilters>(filters);

  const statusOptions: { value: LeadStatus; label: string }[] = [
    { value: 'uncontacted', label: 'Uncontacted' },
    { value: 'cold', label: 'Cold' },
    { value: 'warm', label: 'Warm' },
    { value: 'hot', label: 'Hot' },
    { value: 'enrolled', label: 'Enrolled' },
    { value: 'lost', label: 'Lost' },
    { value: 'dnc', label: 'Do Not Contact' }
  ];

  const determinationOptions: { value: LeadDetermination; label: string }[] = [
    { value: 'pending', label: 'Pending' },
    { value: 'valid', label: 'Valid' },
    { value: 'invalid', label: 'Invalid' }
  ];

  const handleStatusChange = (status: LeadStatus, checked: boolean) => {
    const newStatuses = checked
      ? [...(localFilters.status || []), status]
      : (localFilters.status || []).filter(s => s !== status);
    
    const updatedFilters = { ...localFilters, status: newStatuses };
    setLocalFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const handleDeterminationChange = (determination: LeadDetermination, checked: boolean) => {
    const newDeterminations = checked
      ? [...(localFilters.determination || []), determination]
      : (localFilters.determination || []).filter(d => d !== determination);
    
    const updatedFilters = { ...localFilters, determination: newDeterminations };
    setLocalFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    const date = value ? new Date(value) : null;
    const updatedDateRange = {
      ...(localFilters.dateRange || { start: null, end: null }),
      [field]: date
    };
    
    const updatedFilters = { ...localFilters, dateRange: updatedDateRange };
    setLocalFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const handleSchoolChange = (segmentId: string) => {
    const updatedFilters = { ...localFilters, schoolSegmentId: segmentId || undefined };
    setLocalFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const clearFilters = () => {
    const clearedFilters: LeadFilters = {
      search: '',
      status: [],
      determination: [],
      dateRange: { start: null, end: null },
      schoolSegmentId: undefined
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const activeFilterCount =
    (localFilters.status?.length || 0) +
    (localFilters.determination?.length || 0) +
    (localFilters.dateRange?.start || localFilters.dateRange?.end ? 1 : 0) +
    (localFilters.schoolSegmentId ? 1 : 0);

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          Filters {activeFilterCount > 0 && (
            <span className="ml-2 text-sm text-gray-500">({activeFilterCount} active)</span>
          )}
        </h3>
        {activeFilterCount > 0 && (
          <button
            onClick={clearFilters}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Clear all
          </button>
        )}
      </div>

      {/* School / Campus Filter (multi-school projects only) */}
      {segments && segments.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">School / Campus</h4>
          <div className="flex items-center gap-2 max-w-xs">
            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${segmentDotClass(segments.find(s => s.id === localFilters.schoolSegmentId)?.color)}`} />
            <select
              value={localFilters.schoolSegmentId || ''}
              onChange={(e) => handleSchoolChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All schools</option>
              {segments.map(seg => (
                <option key={seg.id} value={seg.id}>{seg.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Status Filter */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Status</h4>
          <div className="space-y-2">
            {statusOptions.map(option => (
              <label key={option.value} className="flex items-center">
                <input
                  type="checkbox"
                  checked={(localFilters.status || []).includes(option.value)}
                  onChange={(e) => handleStatusChange(option.value, e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Determination Filter */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Determination</h4>
          <div className="space-y-2">
            {determinationOptions.map(option => (
              <label key={option.value} className="flex items-center">
                <input
                  type="checkbox"
                  checked={(localFilters.determination || []).includes(option.value)}
                  onChange={(e) => handleDeterminationChange(option.value, e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Date Range Filter */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Date Range</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">From</label>
              <input
                type="date"
                value={localFilters.dateRange?.start ? format(localFilters.dateRange.start, 'yyyy-MM-dd') : ''}
                onChange={(e) => handleDateRangeChange('start', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">To</label>
              <input
                type="date"
                value={localFilters.dateRange?.end ? format(localFilters.dateRange.end, 'yyyy-MM-dd') : ''}
                onChange={(e) => handleDateRangeChange('end', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadFiltersPanel;